import { Dispatch, FC, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { FreeTrialSubscriptionTypes } from '@frontend/api-free-trial-subscription';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { Checkbox, Chip, Table, TableColumnConfig, Text, useControlledField } from '@frontend/design-system';
import { checkCanStartTrial } from '../../../self-upgrade.util';
import { LocationSubscriptionData } from '../../../types';

interface MultiFreeTrialTableProps {
  data: LocationSubscriptionData[];
  featureName?: string;
  initialSelectedData: LocationSubscriptionData[];
  onChangeSelection: (locationIds: string[]) => void;
}

export const MultiFreeTrialTable: FC<MultiFreeTrialTableProps> = ({
  data = [],
  featureName,
  initialSelectedData,
  onChangeSelection,
}) => {
  const { accessibleLocationData } = useAppScopeStore();
  const { t } = useTranslation('featurePromotion', { keyPrefix: 'free-trial-location-selection-table' });

  const [selectedLocationMap, setSelectedLocationMap] = useState<Record<string, boolean>>({});
  const selectionInitialisedRef = useRef(false);

  const columns = useMemo<TableColumnConfig<LocationSubscriptionData>[]>(() => {
    return [
      {
        id: 'select',
        Header: '',
        accessor: (rowData): { isSelectable: boolean; locationID: string } => ({
          isSelectable: checkCanStartTrial(rowData),
          locationID: rowData.locationId,
        }),
        cellRenderer: ({ isSelectable, locationID }: { isSelectable: boolean; locationID: string }) => {
          if (isSelectable) {
            return (
              <SelectCell
                locationId={locationID}
                selectedLocationMap={selectedLocationMap}
                setSelectedLocationMap={setSelectedLocationMap}
              />
            );
          }
          return <Icon name='lock' color='disabled' />;
        },
        disableSortBy: true,
        maxWidth: 60,
      },
      {
        id: 'location',
        Header: t('Location'),
        accessor: (rowData): { locationName?: string; isRowDisabled: boolean } => ({
          locationName: accessibleLocationData[rowData.locationId]?.name,
          isRowDisabled: !checkCanStartTrial(rowData),
        }),
        cellRenderer: ({ locationName, isRowDisabled }: { locationName: string; isRowDisabled: boolean }) => {
          if (isRowDisabled) {
            return (
              <Chip variant='disabled' leftElement={<Icon name='location-small' />} css={{ maxWidth: 240 }}>
                {locationName}
              </Chip>
            );
          }
          return <Chip.SingleChip css={{ maxWidth: 240 }}>{locationName}</Chip.SingleChip>;
        },
        sortType: (rowA, rowB) => {
          return (accessibleLocationData[rowA.original.locationId]?.name || '')?.localeCompare(
            accessibleLocationData[rowB.original.locationId]?.name || ''
          );
        },
        maxWidth: 350,
      },
      {
        id: 'status',
        Header: t('Free Trial Eligibility'),
        accessor: (rowData) => rowData,
        cellRenderer: (rowData: LocationSubscriptionData) => {
          if (rowData.isStateActive && rowData.trialStatus !== FreeTrialSubscriptionTypes.TrialStatus.TRIAL_ACTIVE) {
            return (
              <Chip leftElement={<Icon name='verified-small' />} variant='eggplant' css={styles.trialChip}>
                {t('{{featureName}} Active', { featureName })}
              </Chip>
            );
          } else if (rowData.trialStatus === FreeTrialSubscriptionTypes.TrialStatus.TRIAL_STATUS_UNSPECIFIED) {
            return (
              <Chip leftElement={<Icon name='block-small' />} variant='disabled' css={styles.trialChip}>
                {t('Ineligible')}
              </Chip>
            );
          } else if (rowData.trialStatus === FreeTrialSubscriptionTypes.TrialStatus.TRIAL_ACTIVE) {
            const daysRemaining = dayjs(rowData.trialEndDate).diff(dayjs(), 'day') + 1;
            return (
              <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
                <Chip leftElement={<Icon name='in-progress-small' />} variant='indigo' css={styles.trialChip}>
                  {t('In Progress')}
                </Chip>
                {daysRemaining > 0 && (
                  <Text size='medium'>{t('{{count}} days remaining on trial', { count: daysRemaining })}</Text>
                )}
              </div>
            );
          } else if (!rowData.canManageAddons) {
            return (
              <Chip leftElement={<Icon name='block-small' />} variant='disabled' css={styles.trialChip}>
                {t('Needs Admin Access')}
              </Chip>
            );
          }
          return (
            <Chip leftElement={<Icon name='check-please-small' />} variant='success' css={styles.trialChip}>
              {t('Eligible')}
            </Chip>
          );
        },
        disableSortBy: true,
      },
    ];
  }, [accessibleLocationData, featureName, selectedLocationMap, t]);

  const modifiedData = useMemo(() => {
    return data.sort((a, b) => {
      const priorityA = checkCanStartTrial(a) ? 0 : 1;
      const priorityB = checkCanStartTrial(b) ? 0 : 1;
      return priorityA - priorityB;
    });
  }, [data]);

  useEffect(() => {
    onChangeSelection(
      Object.entries(selectedLocationMap)
        .filter(([, isSelected]) => isSelected)
        .map(([locationId]) => locationId)
    );
  }, [selectedLocationMap]);

  useEffect(() => {
    if (!selectionInitialisedRef.current) {
      setSelectedLocationMap(
        initialSelectedData.filter(checkCanStartTrial).reduce<Record<string, boolean>>((acc, { locationId }) => {
          acc[locationId] = true;
          return acc;
        }, {})
      );
      selectionInitialisedRef.current = true;
    }
  }, [initialSelectedData]);

  return <Table<LocationSubscriptionData> colConfig={columns} data={modifiedData} />;
};

type SelectCellProps = {
  locationId: string;
  selectedLocationMap: Record<string, boolean>;
  setSelectedLocationMap: Dispatch<SetStateAction<Record<string, boolean>>>;
};

const SelectCell = ({ locationId, selectedLocationMap, setSelectedLocationMap }: SelectCellProps) => {
  const checkboxProps = useControlledField({
    type: 'checkbox',
    value: !!selectedLocationMap[locationId],
    onChange: () =>
      setSelectedLocationMap((state) => ({
        ...state,
        [locationId]: !state[locationId],
      })),
  });
  return <Checkbox {...checkboxProps} name={`${locationId}-select`} error={false} />;
};

const styles = {
  trialChip: css`
    max-width: 320px;
  `,
};
